import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import { TableBody, TableCell, TableContainer, TableFullWidthRow, TableGrid, TableRow } from '~/ui/core/table/Table';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { StatusIndicator } from '../../components/StatusIndicator';
import type { useAuditTableLabels } from '../use-audit-table-labels';
import { renderUserPrimary } from './AuditActorCell';
import { AuditStamp } from './AuditStamp';
import { ACTION_BAR_AUDIT_COLUMN_WIDTHS, ACTION_BAR_LINE_WRAPPER_CLASS, ACTION_BAR_ROW_INSET_CLASS } from './constants';
import {
    computeRowVisuals,
    getActionBarSecondaryTextClass,
    getActionBarStepTextClass,
    getActionBarStepWeight,
    shouldMuteActionBarStep,
} from './row-visuals';
import type { AuditRowComputed, AuditStepId, AuditTableStatus, AuditTrailStep } from './types';
import type { RenderNoteSlotArgs } from './VersionNoteSlot';
import { VersionNoteSlot } from './VersionNoteSlot';

const ActionBarAuditStepSummary: FunctionComponent<{
    readonly actorNode: ReactNode;
    readonly computed: AuditRowComputed;
    readonly step: AuditTrailStep;
}> = ({ actorNode, computed, step }) => {
    const textClass = getActionBarStepTextClass(step, computed);
    const secondaryTextClass = getActionBarSecondaryTextClass(step);
    const weight = getActionBarStepWeight(step);

    return (
        <span className={`flex min-w-0 items-center gap-xs overflow-hidden text-xs ${textClass}`}>
            <StatusIndicator
                className={`shrink-0 ${textClass}`}
                icon={
                    <AuditStamp
                        isNext={computed.isNextLifecycleStep}
                        muted={shouldMuteActionBarStep(step)}
                        size={14}
                        step={step}
                    />
                }
                size="xs"
                weight={weight}
            >
                {computed.stampLabel}
            </StatusIndicator>
            <span className={`shrink-0 ${secondaryTextClass}`}>by</span>
            <span className="inline-flex min-w-0 flex-1 items-center">{actorNode}</span>
        </span>
    );
};

const VersionCommentTableRow: FunctionComponent<{
    readonly autoHeight: boolean;
    readonly children: ReactNode;
    readonly columnWidths: readonly string[];
    readonly isEditing: boolean;
}> = ({ autoHeight, children, columnWidths, isEditing }) => (
    <TableFullWidthRow
        $autoHeight={autoHeight}
        $cellSize="small"
        $columnWidths={columnWidths}
        $size="small"
        cellClassName={`${isEditing ? '!items-stretch !pb-0 !pt-[calc(var(--spacing-s)+var(--spacing-m)-var(--spacing-xxs))]' : '!items-center !py-xs'} !min-h-[34px] !justify-start !gap-xs ${ACTION_BAR_ROW_INSET_CLASS}`}
        className="!mt-0 !rounded-none !bg-transparent shadow-[inset_0_1px_0_var(--color-gray-200)]"
    >
        <span aria-hidden={true} className="shrink-0 basis-[14px]" />
        <span className={isEditing ? 'min-w-0 flex-1' : 'flex min-h-[26px] min-w-0 flex-1 items-center'}>
            {children}
        </span>
    </TableFullWidthRow>
);

export const ActionBarVersionComment: FunctionComponent<
    Omit<RenderNoteSlotArgs, 'versionNumber'> & {
        readonly currentVersion: JobProfileVersion;
    }
> = ({ architectureId, canEditNote, currentVersion, isEditingNote, item, note, onEnterEditMode, onExitEditMode }) => {
    const [isDisplayExpanded, setIsDisplayExpanded] = useState(false);

    useEffect(() => {
        if (isEditingNote) {
            setIsDisplayExpanded(false);
        }
    }, [isEditingNote]);

    if (!is.nonEmptyString(note) && !canEditNote) {
        return null;
    }

    return (
        <VersionCommentTableRow
            autoHeight={isEditingNote || isDisplayExpanded}
            columnWidths={ACTION_BAR_AUDIT_COLUMN_WIDTHS}
            isEditing={isEditingNote}
        >
            <VersionNoteSlot
                architectureId={architectureId}
                canEditNote={canEditNote}
                isEditingNote={isEditingNote}
                item={item}
                note={note}
                showInlineEditAction={true}
                versionNumber={currentVersion.version}
                onDisplayExpandedChange={setIsDisplayExpanded}
                onEnterEditMode={onEnterEditMode}
                onExitEditMode={onExitEditMode}
            />
        </VersionCommentTableRow>
    );
};

type ActionBarAuditRowProps = {
    readonly architectureId: string;
    readonly columnWidths: readonly string[];
    readonly computed: AuditRowComputed;
    readonly isArchived: boolean;
    readonly isCollapsed: boolean;
    readonly item: JobItemDetails;
    readonly noPublisherLabel: string;
    readonly onInviteReviewer?: () => void;
    readonly pendingLabel: string;
    readonly startsVisibleGroup: boolean;
    readonly step: AuditTrailStep;
    readonly trailingNode?: ReactNode;
    readonly unknownUserLabel: string;
};

const ActionBarAuditRow: FunctionComponent<ActionBarAuditRowProps> = ({
    architectureId,
    columnWidths,
    computed,
    isArchived,
    isCollapsed,
    item,
    noPublisherLabel,
    onInviteReviewer,
    pendingLabel,
    startsVisibleGroup,
    step,
    trailingNode,
    unknownUserLabel,
}) => {
    const textClass = getActionBarStepTextClass(step, computed);
    const secondaryTextClass = getActionBarSecondaryTextClass(step);
    const rowBgClass = shouldMuteActionBarStep(step) ? '' : computed.rowBgClass;
    const actorNode = renderUserPrimary(
        step,
        architectureId,
        item,
        isArchived,
        onInviteReviewer,
        secondaryTextClass,
        unknownUserLabel,
        pendingLabel,
        noPublisherLabel,
        computed.showReviewerPill,
        '!h-[22px]',
        'text-xs',
    );
    const resolvedTrailingNode = trailingNode ?? (
        <span
            className={`inline-flex min-h-[24px] max-w-full items-center truncate text-xs font-medium tracking-tight tabular-nums ${textClass}`}
            title={computed.whenContent}
        >
            {computed.whenContent}
        </span>
    );

    return (
        <TableRow
            $animateLayout={false}
            $collapsed={isCollapsed}
            $columnWidths={columnWidths}
            $size="small"
            $startsVisibleGroup={startsVisibleGroup}
            aria-hidden={isCollapsed}
            className={`group ${rowBgClass}`}
        >
            <TableCell
                $size="small"
                className={`!min-h-[34px] !min-w-0 !justify-start !py-xs ${ACTION_BAR_ROW_INSET_CLASS}`}
                role="gridcell"
            >
                <span className="flex w-full min-w-0 items-center justify-start">
                    <span className={ACTION_BAR_LINE_WRAPPER_CLASS}>
                        <ActionBarAuditStepSummary actorNode={actorNode} computed={computed} step={step} />
                    </span>
                </span>
            </TableCell>
            <TableCell
                $size="small"
                className={`!min-h-[34px] !justify-end !py-xs ${ACTION_BAR_ROW_INSET_CLASS}`}
                role="gridcell"
            >
                {resolvedTrailingNode}
            </TableCell>
        </TableRow>
    );
};

export const ActionBarAuditTrailTable: FunctionComponent<{
    readonly architectureId: string;
    readonly commentNode: ReactNode;
    readonly formatTimestamp: (iso: string | null) => string;
    readonly isArchived: boolean;
    readonly item: JobItemDetails;
    readonly labels: ReturnType<typeof useAuditTableLabels>;
    readonly onInviteReviewer?: () => void;
    readonly status: AuditTableStatus;
    readonly steps: readonly AuditTrailStep[];
    readonly trailingNodeForStep?: (step: AuditTrailStep) => ReactNode | undefined;
    readonly visibleStepIds?: readonly AuditStepId[];
}> = ({
    architectureId,
    commentNode,
    formatTimestamp,
    isArchived,
    item,
    labels,
    onInviteReviewer,
    status,
    steps,
    trailingNodeForStep,
    visibleStepIds,
}) => {
    const visibleStepIdSet = visibleStepIds === undefined ? undefined : new Set(visibleStepIds);
    const visibleSteps = visibleStepIdSet === undefined ? steps : steps.filter((step) => visibleStepIdSet.has(step.id));

    return (
        <TableContainer
            $flushBlockEnd={commentNode !== null}
            $inset="none"
            className="!h-auto !rounded-none !bg-transparent"
        >
            <TableGrid aria-label={labels.ariaLabel} className="!rounded-none !overflow-visible" role="grid">
                <TableBody className="!overflow-visible">
                    {visibleSteps.map((step, visibleRowIndex) => (
                        <ActionBarAuditRow
                            key={step.id}
                            architectureId={architectureId}
                            columnWidths={ACTION_BAR_AUDIT_COLUMN_WIDTHS}
                            computed={computeRowVisuals(
                                step,
                                status,
                                undefined,
                                false,
                                labels.pending,
                                formatTimestamp,
                                labels.labelForStep,
                            )}
                            isArchived={isArchived}
                            isCollapsed={false}
                            item={item}
                            noPublisherLabel={labels.noPublisher}
                            pendingLabel={labels.pending}
                            startsVisibleGroup={visibleRowIndex === 0}
                            step={step}
                            trailingNode={trailingNodeForStep?.(step)}
                            unknownUserLabel={labels.unknownUser}
                            onInviteReviewer={onInviteReviewer}
                        />
                    ))}
                    {commentNode}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};
